import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';

const fmtErr = (d, fallback) => {
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc || []).slice(-1)[0] || 'field'}: ${e.msg}` : JSON.stringify(e)).join('; ');
  if (typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
  return String(d);
};

// Read token from URL once and strip it from history immediately so it does
// not appear in browser history, server logs, or Referer headers.
function consumeToken() {
  const p = new URLSearchParams(window.location.search);
  const t = p.get('token') || '';
  if (t) window.history.replaceState(null, '', window.location.pathname);
  return t;
}
const _TOKEN = consumeToken();

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = _TOKEN || params.get('token') || '';

  const [pwd, setPwd] = useState({ password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [doneUser, setDoneUser] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.password !== pwd.confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      const resp = await authAPI.acceptInvite(token, pwd.password);
      setDoneUser(resp?.data?.username || resp?.username || '');
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail, 'This invite link is invalid or has expired.'));
    } finally {
      setLoading(false);
    }
  };

  const input = (focused) => ({
    width: '100%', boxSizing: 'border-box', padding: '11px 13px',
    borderRadius: 10, fontSize: 14, color: '#2b1b22', fontFamily: 'Inter, sans-serif',
    outline: 'none', background: '#fff',
    border: `1px solid ${focused ? '#7b1e3c' : '#ece3e7'}`,
  });

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f4f6fa', fontFamily: 'Inter, sans-serif', padding: '40px 16px',
    }}>
      <div style={{
        width: '100%', maxWidth: 440, background: '#fff', borderRadius: 16,
        padding: '38px 36px', border: '1px solid #ece3e7',
        boxShadow: '0 1px 2px rgba(13,27,46,0.04), 0 12px 32px -8px rgba(13,27,46,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: 'linear-gradient(135deg,#7b1e3c,#c39a4e)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 19, fontWeight: 900, color: '#fff',
          }}>H</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#2b1b22' }}>HHMS</div>
        </div>

        {doneUser ? (
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2b1b22', margin: '0 0 10px' }}>
              You're in ✓
            </h1>
            <p style={{ fontSize: 13.5, color: '#8b7681', lineHeight: 1.65, margin: '0 0 20px' }}>
              Your account <code style={{
                background: '#f7f3f4', border: '1px solid #ece3e7', borderRadius: 4,
                padding: '1px 6px', fontSize: 12 }}>{doneUser}</code> is active.
              Sign in with the password you just set.
            </p>
            <button onClick={() => navigate('/login')} style={{
              padding: '11px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(180deg,#94335a,#7b1e3c)', color: '#fff',
              fontSize: 13.5, fontWeight: 700, fontFamily: 'Inter, sans-serif',
            }}>Go to sign in</button>
          </div>
        ) : !token ? (
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2b1b22', margin: '0 0 10px' }}>
              Invalid invite link
            </h1>
            <p style={{ fontSize: 13.5, color: '#8b7681', lineHeight: 1.6 }}>
              This link is missing its invite token. Ask your administrator to send a new one.
            </p>
            <Link to="/login" style={{ color: '#7b1e3c', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2b1b22', margin: '0 0 6px' }}>
              Welcome to Benevita
            </h1>
            <p style={{ fontSize: 13, color: '#8b7681', lineHeight: 1.6, margin: '0 0 20px' }}>
              You've been invited to HHMS. Set your password to activate your account —
              at least 10 characters with upper &amp; lower case, a digit and a symbol.
            </p>

            {error && (
              <div role="alert" style={{
                background: '#fdecea', color: '#b71c1c', padding: '10px 13px',
                borderRadius: 10, marginBottom: 16, fontSize: 12.5,
                border: '1px solid #f5c2c0', lineHeight: 1.5,
              }}>{error}</div>
            )}

            <form onSubmit={submit} noValidate>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#1e3348',
                  marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  New password
                </label>
                <input type="password" value={pwd.password} autoComplete="new-password"
                  onChange={e => setPwd({ ...pwd, password: e.target.value })}
                  placeholder="Choose a strong password" style={input(false)} />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#1e3348',
                  marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Confirm password
                </label>
                <input type="password" value={pwd.confirm} autoComplete="new-password"
                  onChange={e => setPwd({ ...pwd, confirm: e.target.value })}
                  placeholder="Repeat the password" style={input(false)} />
              </div>
              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '12px 16px',
                background: loading ? '#90a4ae' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}>
                {loading ? 'Activating…' : 'Activate my account'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
